import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../../../auth/adminPermissions";
import {
  buildVehicleModelCopyQualityExportUrl,
  createVehicleModelCopyQualitySnapshot,
  fetchVehicleModelCopyQualityIssues,
  fetchVehicleModelCopyQualityModels,
  fetchVehicleModelCopyQualitySummary,
  fetchVehicleModelCopyQualityTrends,
} from "../../../../services/vehicleModelCopyAdminApi";
import AdminStatePanel from "../layout/AdminStatePanel";
import AdminTableContainer from "../layout/AdminTableContainer";

function formatPercent(value, denominator) {
  if (!denominator) return "0%";
  return `${Math.round((value / denominator) * 1000) / 10}%`;
}

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function severityClass(severity) {
  if (severity === "critical") return "admin-badge--danger";
  if (severity === "high") return "admin-badge--warn";
  if (severity === "medium") return "";
  return "admin-badge--muted";
}

const VehicleModelCopyQualityPage = ({ onClose, onOpenCopyEditor }) => {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canView =
    isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_VIEW);
  const canManage =
    isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);

  const [summary, setSummary] = useState(null);
  const [models, setModels] = useState([]);
  const [issues, setIssues] = useState([]);
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [toast, setToast] = useState("");

  const [universe, setUniverse] = useState("active_inventory");
  const [severity, setSeverity] = useState("");
  const [issueType, setIssueType] = useState("");
  const [coverageState, setCoverageState] = useState("");

  const filters = {
    universe,
    severity: severity || undefined,
    issueType: issueType || undefined,
    coverageState: coverageState || undefined,
  };

  const load = useCallback(async () => {
    if (!canView) return;
    try {
      setLoading(true);
      setErrorMessage("");
      const [summaryData, modelData, issueData, trendData] = await Promise.all([
        fetchVehicleModelCopyQualitySummary(filters),
        fetchVehicleModelCopyQualityModels({ ...filters, limit: 25, sort: "-listingCount" }),
        fetchVehicleModelCopyQualityIssues({ ...filters, limit: 50, sort: "-activeListingCount" }),
        fetchVehicleModelCopyQualityTrends({ universe }),
      ]);
      setSummary(summaryData);
      setModels(modelData.items || []);
      setIssues(issueData.items || []);
      setTrends(trendData.trends || []);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load quality report.");
      setSummary(null);
      setModels([]);
      setIssues([]);
    } finally {
      setLoading(false);
    }
  }, [canView, universe, severity, issueType, coverageState]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSnapshot = async () => {
    if (!canManage) return;
    try {
      const result = await createVehicleModelCopyQualitySnapshot({ filters });
      setToast(result.saved ? "Quality snapshot saved." : "Snapshot preview generated.");
      await load();
    } catch (error) {
      setToast(error.message || "Snapshot failed.");
    }
  };

  const openEditor = (item) => {
    onOpenCopyEditor?.({
      vehicleModelId: item.vehicleModelId,
      makeKey: item.makeKey,
      modelKey: item.modelKey,
      makeName: item.makeName,
      modelName: item.modelName,
    });
  };

  if (!canView) {
    return (
      <AdminStatePanel
        title="Vehicle model copy quality"
        message="You do not have permission to view quality reports."
      />
    );
  }

  const summaryData = summary?.summary;

  return (
    <div className="admin-dashboard admin-vehicle-model-copy-quality-dash admin-vehicle-sources-dash--full-width">
      <header className="admin-dashboard__header">
        <div>
          <p className="admin-dashboard__eyebrow">Mobility · Vehicle Intelligence</p>
          <h1 className="admin-dashboard__title">Vehicle Model Copy Quality</h1>
          <p className="admin-dashboard__subtitle">
            Coverage, publication health, and catalog integrity against active inventory.
          </p>
          {summary?.dataFreshness ? (
            <p className="muted">
              Generated {formatDate(summary.generatedAt)} · Inventory data{" "}
              {formatDate(summary.dataFreshness.latestInventoryAt)} · Copy data{" "}
              {formatDate(summary.dataFreshness.latestCopyAt)}
            </p>
          ) : null}
        </div>
        <div className="admin-dashboard__header-actions">
          <a
            className="admin-dashboard__action-btn"
            href={buildVehicleModelCopyQualityExportUrl("csv", filters)}
          >
            Export CSV
          </a>
          <a
            className="admin-dashboard__action-btn"
            href={buildVehicleModelCopyQualityExportUrl("json", filters)}
          >
            Export JSON
          </a>
          {canManage ? (
            <button type="button" className="admin-dashboard__action-btn" onClick={handleSnapshot}>
              Save snapshot
            </button>
          ) : null}
          <button type="button" className="admin-dashboard__action-btn" onClick={load}>
            Refresh
          </button>
          {onClose ? (
            <button type="button" className="admin-dashboard__action-btn" onClick={onClose}>
              Close
            </button>
          ) : null}
        </div>
      </header>

      {toast ? (
        <p className="admin-dashboard__toast" role="status">
          {toast}
        </p>
      ) : null}

      <section className="panel-card admin-vehicle-model-copy-quality-dash__filters">
        <label>
          Universe
          <select
            aria-label="Universe"
            value={universe}
            onChange={(event) => setUniverse(event.target.value)}
          >
            <option value="active_inventory">Active inventory</option>
            <option value="canonical_catalog">Canonical catalog</option>
            <option value="copy_record">Copy records</option>
          </select>
        </label>
        <label>
          Coverage
          <select value={coverageState} onChange={(event) => setCoverageState(event.target.value)}>
            <option value="">Any</option>
            <option value="COMPLETE">Complete</option>
            <option value="PARTIAL">Partial</option>
            <option value="MISSING">Missing</option>
            <option value="DRAFT_ONLY">Draft only</option>
            <option value="UNRESOLVED">Unresolved</option>
          </select>
        </label>
        <label>
          Severity
          <select value={severity} onChange={(event) => setSeverity(event.target.value)}>
            <option value="">Any</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="informational">Informational</option>
          </select>
        </label>
        <label>
          Issue type
          <input
            type="search"
            value={issueType}
            onChange={(event) => setIssueType(event.target.value)}
            placeholder="MISSING_LOCALE"
          />
        </label>
      </section>

      {errorMessage ? (
        <AdminStatePanel title="Unable to load quality report" message={errorMessage} onRetry={load} />
      ) : null}

      {loading ? <p className="muted">Loading quality report…</p> : null}

      {!loading && summaryData ? (
        <>
          <section className="admin-vehicle-model-copy-quality-dash__kpis panel-card">
            <article>
              <h2>Active unique models</h2>
              <p>{summaryData.totalModels}</p>
            </article>
            <article>
              <h2>Active listings</h2>
              <p>{summaryData.totalListings}</p>
            </article>
            <article>
              <h2>Published DB coverage</h2>
              <p>
                {summaryData.publishedDbModels} / {summaryData.totalModels} (
                {formatPercent(summaryData.publishedDbModels, summaryData.totalModels)})
              </p>
            </article>
            <article>
              <h2>Complete locale coverage</h2>
              <p>
                {summaryData.completeLocaleModels} / {summaryData.totalModels} (
                {formatPercent(summaryData.completeLocaleModels, summaryData.totalModels)})
              </p>
            </article>
            <article>
              <h2>Draft-only models</h2>
              <p>
                {summaryData.draftOnlyModels} / {summaryData.totalModels} (
                {formatPercent(summaryData.draftOnlyModels, summaryData.totalModels)})
              </p>
            </article>
            <article>
              <h2>Unresolved models</h2>
              <p>
                {summaryData.unresolvedModels} / {summaryData.totalModels} (
                {formatPercent(summaryData.unresolvedModels, summaryData.totalModels)})
              </p>
            </article>
            <article>
              <h2>Critical issues</h2>
              <p>{summaryData.criticalIssueCount}</p>
            </article>
            <article>
              <h2>Drafts awaiting publication</h2>
              <p>{summaryData.draftsAwaitingPublication}</p>
            </article>
          </section>

          <section className="panel-card">
            <h2>Locale coverage (unique models)</h2>
            <table className="admin-data-health-table">
              <thead>
                <tr>
                  <th>Locale</th>
                  <th>Published</th>
                  <th>Draft</th>
                  <th>Missing</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(summaryData.localeCoverage || {}).map(([locale, counts]) => (
                  <tr key={locale}>
                    <td>{locale}</td>
                    <td>
                      {counts.published} ({formatPercent(counts.published, summaryData.totalModels)})
                    </td>
                    <td>{counts.draft}</td>
                    <td>{counts.missing}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="panel-card">
            <h2>Catalog health readiness</h2>
            <ul>
              <li>
                Published DB unique-model coverage:{" "}
                {formatPercent(summaryData.readiness?.publishedDbUniqueModelCoverage || 0, 1)}
              </li>
              <li>
                Listing-weighted DB coverage:{" "}
                {formatPercent(summaryData.readiness?.listingWeightedDbCoverage || 0, 1)}
              </li>
              <li>
                Complete locale coverage:{" "}
                {formatPercent(summaryData.readiness?.completeLocaleCoverage || 0, 1)}
              </li>
              <li>
                Unresolved model rate:{" "}
                {formatPercent(summaryData.readiness?.unresolvedModelRate || 0, 1)}
              </li>
            </ul>
          </section>

          {trends.length ? (
            <section className="panel-card">
              <h2>Recent snapshots</h2>
              <table className="admin-data-health-table">
                <thead>
                  <tr>
                    <th>Generated</th>
                    <th>Models</th>
                    <th>Published DB</th>
                    <th>Draft only</th>
                    <th>Critical issues</th>
                  </tr>
                </thead>
                <tbody>
                  {trends.slice(0, 10).map((point) => (
                    <tr key={point.generatedAt}>
                      <td>{formatDate(point.generatedAt)}</td>
                      <td>{point.totalModels}</td>
                      <td>{point.publishedDbModels}</td>
                      <td>{point.draftOnlyModels ?? point.summary?.draftOnlyModels ?? 0}</td>
                      <td>{point.criticalIssueCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ) : null}

          <AdminTableContainer>
            <h2>Highest-impact models</h2>
            <table className="admin-data-health-table">
              <thead>
                <tr>
                  <th>Make</th>
                  <th>Model</th>
                  <th>Listings</th>
                  <th>Coverage</th>
                  <th>Resolution</th>
                  <th>Issues</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {models.map((item) => (
                  <tr key={item.identityKey}>
                    <td>{item.makeName}</td>
                    <td>{item.modelName}</td>
                    <td>{item.activeListingCount}</td>
                    <td>{item.coverageState}</td>
                    <td>{item.resolutionMethod}</td>
                    <td>{item.issueCount}</td>
                    <td>
                      <button
                        type="button"
                        className="admin-dashboard__action-btn"
                        onClick={() => openEditor(item)}
                      >
                        Open copy editor
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableContainer>

          <AdminTableContainer>
            <h2>Quality issues</h2>
            <table className="admin-data-health-table">
              <thead>
                <tr>
                  <th>Severity</th>
                  <th>Issue</th>
                  <th>Make / Model</th>
                  <th>Locale</th>
                  <th>Listings</th>
                  <th>Action</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {issues.map((issue, index) => (
                  <tr key={`${issue.identityKey}-${issue.issueType}-${issue.locale || "all"}-${index}`}>
                    <td>
                      <span className={`admin-badge ${severityClass(issue.severity)}`}>
                        {issue.severity}
                      </span>
                    </td>
                    <td>{issue.issueType}</td>
                    <td>
                      {issue.makeName} {issue.modelName}
                    </td>
                    <td>{issue.locale || "—"}</td>
                    <td>{issue.activeListingCount}</td>
                    <td>{issue.recommendedAction}</td>
                    <td>
                      <button
                        type="button"
                        className="admin-dashboard__action-btn"
                        onClick={() => openEditor(issue)}
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableContainer>
        </>
      ) : null}
    </div>
  );
};

export default VehicleModelCopyQualityPage;
